/* eslint-disable sonarjs/cognitive-complexity, max-lines-per-function */
import React, { MutableRefObject, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash-es';
import { getPaidNewsletterCreatorStore, ModifyPaidNewsletterMetaData, SaveStart, SetIsTemplateOpen, SetPaidNewsletterErrors, StartEditStep, TemplateDeselect, ModifyEmailTemplate, ModifySubscriptionCancelationTemplate } from '@/store/paidNewsletterCreator/actions';
import { EmailTemplateType, PaidNewsletterCreatorStatus, PaidNewsletterErrorNames} from '@/store/paidNewsletterCreator/types';
import { useAccessLevel } from '@/services/hooks/useAccessLevel';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { ShowDialogParams } from '@/store/dialog/types';
import { ShowDialog } from '@/store/dialog/actions';
import { getAccountAdvancedOptions } from '@/store/account/actions';
import { StepTitle } from '../components/StepTitle';
import { StepContent } from '../components/StepContent';
import { Step1Content } from '../steps/Step1Content';
import { Step1EditContent } from '../steps/Step1EditContent';
import { Step2Content } from '../steps/Step2Content';
import { Step2EditContent } from '../steps/Step2EditContent';
import { getStepsValidity } from '../tools/getStepsValidity';
import { getStepsStatuses } from '../tools/getStepsStatuses';
import { Step3Content } from '../steps/Step3Content';
import { Step3EditContent } from '../steps/Step3EditContent';
import { Step4Content } from '../steps/Step4Content';
import { Step4EditContent } from '../steps/Step4EditContent';
import { Step5Content } from '../steps/Step5Content';
import { useLandingPageSubdomain } from './useLandingPageSubdomain';
import { Step5EditContent } from '../steps/Step5EditContent';
import { isPaidNewsletterCreated } from '../tools/isPaidNewsletterCreated';
import { useGetDefaultSender } from '../../../services/hooks/useGetDefaultSender';
import { FooterContent } from '../steps/FooterContent';
import { Step6Content } from '../steps/Step6Content';
import { Step6EditContent } from '../steps/Step6EditContent';

interface StepsProps {
    onExit: () => void;
    isSavedRef: MutableRefObject<boolean>;
    isGalleryCreator: boolean;
}

export const useSteps = ({ onExit, isSavedRef, isGalleryCreator }: StepsProps): any[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {changeSubdomain, accountSubdomain,paidNewsletterBaseUrl} = useLandingPageSubdomain();
    const advancedOptions = useSelector(getAccountAdvancedOptions);
    const {email: defaultEmail, name: defaultName } = useGetDefaultSender() ?? {};
    const { stepInEdit, paidNewsletterObject, paidNewsletterPayload, template, status, errors, paidNewsletterMetaDataObject, paidNewsletterMetaDataPayload, urlPayload, postPurchaseTemplatePayload, subscriptionCancelationTemplatePayload, openEmailTemplateType } = useSelector(getPaidNewsletterCreatorStore);
    const isCreated = isPaidNewsletterCreated(paidNewsletterObject);
    const isSaving = status === PaidNewsletterCreatorStatus.SAVING;
    const isStep3Visible = !advancedOptions?.IsOwnedByReseller;
    const stepsValidity = getStepsValidity(isStep3Visible, template, paidNewsletterObject, paidNewsletterMetaDataObject);
    const stepsStatuses = getStepsStatuses(stepsValidity, stepInEdit);
    const { hasAccessLevel } = useAccessLevel();
    const canModifyPaidNewsletters = hasAccessLevel(AccessLevel.ModifyPaidNewsletters);
    const step5Ref = useRef<HTMLDivElement>();
    const step6Ref = useRef<HTMLDivElement>();
    const isEmailDesignerOpenRef = useRef<boolean>();

    useEffect(() => {
        if (openEmailTemplateType) { isEmailDesignerOpenRef.current = true; return; }
        if (isEmailDesignerOpenRef.current && openEmailTemplateType === EmailTemplateType.EMAIL) { isEmailDesignerOpenRef.current = false; step5Ref.current?.scrollIntoView?.(); }
        if (isEmailDesignerOpenRef.current && openEmailTemplateType === EmailTemplateType.SUBSCRIPTION_CANCELATION) { isEmailDesignerOpenRef.current = false; step6Ref.current?.scrollIntoView?.(); }
    },[openEmailTemplateType]);   
    
    useEffect((): void => {
        isSavedRef.current = !(paidNewsletterPayload || paidNewsletterMetaDataPayload || urlPayload || postPurchaseTemplatePayload || !stepsValidity['5'] || subscriptionCancelationTemplatePayload ||!stepsValidity['6']);
    }, [paidNewsletterPayload,paidNewsletterObject,paidNewsletterMetaDataPayload,urlPayload, postPurchaseTemplatePayload, subscriptionCancelationTemplatePayload])
    
    const editTemplate = (): void => {dispatch(SetIsTemplateOpen(true));};
    const deselectTemplate = (): void => {
        dispatch(StartEditStep(2));
        dispatch(TemplateDeselect()); // this order is important, first step edit then deselect
    };
    const replaceTemplate = (): void => {
        const dialogParams: ShowDialogParams = {
            title: t('paidnewsletterreplacetemplate'),
            confirmLabel: t('confirm'),
            body: t('paidnewsletterreplacetemplate_description'),
            onClickConfirm: deselectTemplate,
            cancelLabel: t('cancel'),
        }; dispatch(ShowDialog(dialogParams));
    };
    const setCustomLandingPageDomain = () => {
        const { ProductLanding } = paidNewsletterObject;
        const landingUrl = urlPayload ?? paidNewsletterBaseUrl;
        //prevent from going further if there is an error 
        if(errors?.landing_invalid_suffix){
            return;
        }
        if(!accountSubdomain) {
            dispatch(SetPaidNewsletterErrors({
                [PaidNewsletterErrorNames.LACK_OF_ACCOUNT_SUBDOMAIN]: true
            }))
            return;
        }
        return changeSubdomain({LandingPageID: ProductLanding?.LandingPageID, UrlSuffix: landingUrl}).then(()=>{
                dispatch(SaveStart({}));                
            }).catch(({errorKey})=>{
                const errors = {
                ...(errorKey === PaidNewsletterErrorNames.SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE && {[PaidNewsletterErrorNames.SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE]: true}),
                ...(errorKey === PaidNewsletterErrorNames.INVALID_SUFFIX && {[PaidNewsletterErrorNames.INVALID_SUFFIX]: true})
            }
            dispatch(SetPaidNewsletterErrors(errors))
        })
    };
    const setEmptyLandingMetaJson = (): void => {
        if (!paidNewsletterObject?.ProductLanding?.LandingMetaJson && !paidNewsletterMetaDataObject?.data?.socialPreview && !paidNewsletterMetaDataPayload) {
            dispatch(ModifyPaidNewsletterMetaData({ data: { socialPreview: { pageTitle: '', pageDescription: '', imageUrl: '' }} }));
        }
        dispatch(SaveStart({}));
    };
    const getIsStep1SaveDisabled = () => {
        if (!isCreated && (!paidNewsletterPayload?.Name || !paidNewsletterPayload?.ProductPrice)) {
            return true;
        }
        const hasErrors = errors?.DuplicatedName || errors?.InvalidProductPrice;
        if  (!paidNewsletterPayload || hasErrors) {
            return true;
        }
        return paidNewsletterPayload?.Name === '';
    };
    const setDefaultStep5Data = (): void => {
        if (isNil(postPurchaseTemplatePayload?.fromEmail) && !paidNewsletterObject?.PostPurchaseTemplate?.FromEmail) {
            dispatch(ModifyEmailTemplate({ fromEmail: defaultEmail }));
        }
        if (isNil(postPurchaseTemplatePayload?.fromName) && isNil(paidNewsletterObject?.PostPurchaseTemplate?.FromName)) {
            dispatch(ModifyEmailTemplate({ fromName: defaultName }));
        }
        if (isNil(postPurchaseTemplatePayload?.subject) && isNil(paidNewsletterObject?.PostPurchaseTemplate?.Body?.Subject)) {
            dispatch(ModifyEmailTemplate({ subject: t('paidnewsletterpostpurchasedefaultsubject') }));
        }
        dispatch(SaveStart({}));
    };
    const setDefaultStep6Data = (): void => {
        if (isNil(subscriptionCancelationTemplatePayload?.fromEmail) && !paidNewsletterObject?.CancellationTemplate?.FromEmail) {
            dispatch(ModifySubscriptionCancelationTemplate({ fromEmail: defaultEmail }));
        }
        if (isNil(subscriptionCancelationTemplatePayload?.fromName) && isNil(paidNewsletterObject?.CancellationTemplate?.FromName)) {
            dispatch(ModifySubscriptionCancelationTemplate({ fromName: defaultName }));
        }
        if (isNil(subscriptionCancelationTemplatePayload?.subject) && isNil(paidNewsletterObject?.CancellationTemplate?.Body?.Subject)) {
            dispatch(ModifySubscriptionCancelationTemplate({ subject: t('paidnewslettersubscriptioncancelationdefaultsubject') }));
        }
        dispatch(SaveStart({}));
    };
    const isStep5SaveDisabled = 
        postPurchaseTemplatePayload?.fromEmail===''
        || postPurchaseTemplatePayload?.subject === ''
        || !paidNewsletterObject?.PostPurchaseTemplate?.Body?.Parts;
    const isStep6SaveDisabled = 
        subscriptionCancelationTemplatePayload?.fromEmail===''
        || subscriptionCancelationTemplatePayload?.subject === ''
        || !paidNewsletterObject?.CancellationTemplate?.Body?.Parts;
    const isEditAvailable = canModifyPaidNewsletters;
    const isEditDisabled = !isEditAvailable || isSaving;
    const isStep3SaveDisabled = () => {
        const hasErrors = errors?.landing_subdirectory_in_use || errors?.landing_invalid_suffix || errors?.landing_lack_of_account_subdomain;
        const currentUrl = `${paidNewsletterBaseUrl}${urlPayload ?? ''}`;
        const originalUrl = paidNewsletterObject?.ProductLanding?.LandingUrl ?? paidNewsletterBaseUrl;
        const urlNotChanged = currentUrl === originalUrl;
        return hasErrors || urlNotChanged;
    };
    return [
        {
            title: <StepTitle stepNumber={1} isEditVisible={stepsStatuses['1'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('newsletter')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={getIsStep1SaveDisabled()}
                    isEditMode={isEditAvailable && stepsStatuses['1'] === 'active'}
                    isNew={!stepsValidity['1']}
                    content={<Step1Content />}
                    editContent={<Step1EditContent />}
                />
            ),
            status: stepsStatuses['1'],
            hasErrors: stepInEdit === 1 || !stepsValidity['1'],
        },
        {
            title: <StepTitle 
                stepNumber={2} 
                isEditVisible={stepsStatuses['2'] === 'done-open'} 
                isEditDisabled={isEditDisabled} 
                title={t('design')}
                customButtonTitle1={t('replace')}
                customButtonTitle2={t('edittemplate')}
                customButtonAction1={replaceTemplate}
                customButtonAction2={editTemplate}
                skipEditStepWarning1
            />,
            content: (
                <StepContent
                    isSaveVisible={false}
                    isSaveDisabled
                    isEditMode={isEditAvailable && stepsStatuses['2'] === 'active'}
                    isNew={!stepsValidity['2']}
                    content={<Step2Content />}
                    editContent={<Step2EditContent /> }
                />
            ),
            status: stepsStatuses['2'],
            hasErrors: stepInEdit === 2 || !stepsValidity['2'],
        },
        ...(isStep3Visible ? [{
            title: <StepTitle stepNumber={3} isEditVisible={stepsStatuses['3'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('paidnewsletterlink')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={isStep3SaveDisabled()}  
                    isEditMode={isEditAvailable && stepsStatuses['3'] === 'active'}
                    isNew={!stepsValidity['3']}
                    content={<Step3Content />} 
                    editContent={<Step3EditContent />}
                    beforeSave
                    onSave={setCustomLandingPageDomain}
                />
            ),
            status: stepsStatuses['3'],
            hasErrors: stepInEdit === 3 || !stepsValidity['3'],
        }] : []),
        {
            title: <StepTitle stepNumber={4} isEditVisible={stepsStatuses['4'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('lp_creator_social_header')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={false}  
                    isEditMode={isEditAvailable && stepsStatuses['4'] === 'active'}
                    isNew={!stepsValidity['4']}
                    content={<Step4Content />} 
                    editContent={<Step4EditContent />}
                    onSave={setEmptyLandingMetaJson}
                />
            ),
            status: stepsStatuses['4'],
            hasErrors: stepInEdit === 4 || !stepsValidity['4'],
        },
        {
            title: <StepTitle stepNumber={5} isEditVisible={stepsStatuses['5'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('postpurchasesettings')} />,
            content: (
                <StepContent 
                    wrapperRef={step5Ref}
                    isSaveVisible
                    isSaveDisabled={isStep5SaveDisabled}
                    isEditMode={isGalleryCreator || (isEditAvailable && stepsStatuses['5'] === 'active')}
                    isNew={!stepsValidity['5']}
                    content={<Step5Content isEditAvailable={isEditAvailable} />}
                    editContent={<Step5EditContent />}
                    onSave={setDefaultStep5Data}
                />
            ),
            status: isGalleryCreator ? 'active' : stepsStatuses['5'],
            hasErrors: stepInEdit === 5 || !stepsValidity['5'],
        },
        {
            title: <StepTitle stepNumber={6} isEditVisible={stepsStatuses['6'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('subscriptioncancelation')} />,
            content: (
                <StepContent 
                    wrapperRef={step6Ref}
                    isSaveVisible
                    isSaveDisabled={isStep6SaveDisabled}
                    isEditMode={isGalleryCreator || (isEditAvailable && stepsStatuses['6'] === 'active')}
                    isNew={!stepsValidity['6']}
                    content={<Step6Content isEditAvailable={isEditAvailable} />}
                    editContent={<Step6EditContent />}
                    onSave={setDefaultStep6Data}
                />
            ),
            status: isGalleryCreator ? 'active' : stepsStatuses['6'],
            hasErrors: stepInEdit === 6 || !stepsValidity['6'],
        },
        {
            title: '',
            content: <FooterContent onExit={onExit} isReady={stepsValidity['6']} isEditAvailable={isEditAvailable} />,
            status: 'content-only',
            hasErrors: false,
        },
    ];
};
